import { useState } from 'react'
import { FileUp, FileDown } from 'lucide-react'
import { T } from '../../styles/theme'
import CambiarPassword from '../../components/CambiarPassword'
import ExportModal from '../../components/ExportModal'
import ImportModal from '../../components/ImportModal'
import PrintPlan from '../../components/PrintPlan'
import { SectionLabel, Card } from '../../components/ui'

export default function Perfil({ data, signOut }) {
  const { goals, patientId, patientNombre, plan, refetch } = data
  const [showExport, setShowExport] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [showPlan, setShowPlan] = useState(false)

  const tipoLabel = {
    deficit: 'Perder grasa',
    mantenimiento: 'Mantenimiento',
    superavit: 'Ganar músculo',
  }

  const kcalObj = goals?.kcal_objetivo ?? '—'
  const protObj = goals?.proteina_objetivo ?? '—'
  const grasaObj = goals?.grasa_objetivo ?? '—'
  const carbObj = goals?.carbohidratos_objetivo ?? '—'
  const pesoObj = goals?.peso_objetivo ?? '—'
  const grasaPctObj = goals?.grasa_objetivo_pct ?? '—'
  const tipo = goals?.tipo ? tipoLabel[goals.tipo] : '—'

  return (
    <div>
      <div style={{ padding: '52px 20px 12px' }}>
        <div style={{ fontSize: 26, fontWeight: 700, letterSpacing: -0.5 }}>Mi perfil</div>
        <div style={{ fontSize: 13, color: T.muted, marginTop: 2 }}>Objetivos y configuración</div>
      </div>

      <SectionLabel>
        Objetivo
      </SectionLabel>
      <Card>
        <div style={{ fontSize: 18, fontWeight: 700, color: T.accent, marginBottom: 4 }}>{tipo}</div>
        <div style={{ fontSize: 13, color: T.muted }}>Fijado por tu profesional</div>
      </Card>

      <SectionLabel>
        Pauta nutricional diaria
      </SectionLabel>
      <Card style={{ padding: '4px 16px' }}>
        {[
          ['🔥 Calorías', kcalObj ? `${kcalObj} kcal` : '—'],
          ['🥩 Proteína', protObj ? `${protObj} g` : '—'],
          ['🥑 Grasas', grasaObj ? `${grasaObj} g` : '—'],
          ['🍞 Carbohidratos', carbObj ? `${carbObj} g` : '—'],
        ].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 0', borderBottom: `1px solid ${T.cardB}` }}>
            <span style={{ fontSize: 14, color: T.sub }}>{k}</span>
            <span style={{ fontSize: 14, fontWeight: 700, fontFamily: 'DM Mono', color: T.text }}>{v}</span>
          </div>
        ))}
      </Card>

      <SectionLabel>
        Objetivos corporales
      </SectionLabel>
      <Card style={{ padding: '4px 16px' }}>
        {[
          ['⚖️ Peso objetivo', pesoObj ? `${pesoObj} kg` : '—'],
          ['💪 Grasa objetivo', grasaPctObj ? `${grasaPctObj}%` : '—'],
        ].map(([k, v]) => (
          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '13px 0', borderBottom: `1px solid ${T.cardB}` }}>
            <span style={{ fontSize: 14, color: T.sub }}>{k}</span>
            <span style={{ fontSize: 14, fontWeight: 700, fontFamily: 'DM Mono', color: T.text }}>{v}</span>
          </div>
        ))}
      </Card>

      <div style={{ background: T.accentSoft, border: `1px solid ${T.accent}`, borderRadius: 12, padding: '12px 16px', margin: '0 16px 24px', fontSize: 13, color: T.accent, lineHeight: 1.5 }}>
        ℹ️ Los objetivos los ajusta tu profesional según tu evolución. Si tienes dudas, consúltale en tu próxima revisión.
      </div>

      {plan && (
        <>
          <SectionLabel>
            Mi plan
          </SectionLabel>
          <div style={{ margin: '0 16px 24px' }}>
            <button onClick={() => setShowPlan(true)} style={{
              width: '100%', padding: 12, borderRadius: 12,
              background: T.accentSoft, border: `1px solid ${T.accent}`,
              color: T.accent, fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}>
              📄 Ver / descargar mi plan nutricional
            </button>
          </div>
        </>
      )}

      <SectionLabel>
        Mis datos
      </SectionLabel>
      <div style={{ margin: '0 16px 12px' }}>
        <button onClick={() => setShowExport(true)} style={{
          width: '100%', padding: 12, borderRadius: 12,
          background: T.accentSoft, border: `1px solid ${T.accent}`,
          color: T.accent, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          marginBottom: 8,
        }}>
          <FileUp size={16} /> Descargar mis datos
        </button>
        <button onClick={() => setShowImport(true)} style={{
          width: '100%', padding: 12, borderRadius: 12,
          background: 'transparent', border: `1px solid ${T.cardB}`,
          color: T.muted, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <FileDown size={16} /> Restaurar copia de seguridad
        </button>
      </div>

      <SectionLabel>
        Seguridad
      </SectionLabel>
      <Card>
        <CambiarPassword />
      </Card>

      {patientId && (
        <>
          <ExportModal patients={[{ id: patientId, nombre: patientNombre || 'mis_datos' }]} open={showExport} onClose={() => setShowExport(false)} />
          <ImportModal mode="patient" patientId={patientId} open={showImport} onClose={() => setShowImport(false)} onImported={refetch} />
        </>
      )}

      <button onClick={signOut} style={{
        display: 'block', margin: '0 16px 24px', width: 'calc(100% - 32px)',
        padding: 14, borderRadius: 14, background: 'transparent',
        color: T.muted, fontSize: 15, fontWeight: 600,
        border: `1px solid ${T.cardB}`, cursor: 'pointer', fontFamily: 'DM Sans'
      }}>
        Cerrar sesión
      </button>

      {showPlan && (
        <PrintPlan patientNombre={patientNombre} contenido={plan} onClose={() => setShowPlan(false)} />
      )}
    </div>
  )
}